import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAdminRole } from '@/hooks/useAdminRole';
import { use2FA } from '@/hooks/use2FA';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldAlert, Shield } from 'lucide-react';

interface AdminGuardProps {
  children: React.ReactNode;
}

export const AdminGuard: React.FC<AdminGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const { isAdmin, loading } = useAdminRole();
  const { status: mfaStatus } = use2FA();
  const [checking, setChecking] = useState(true);
  
  useEffect(() => {
    if (!loading && !mfaStatus.loading) {
      setChecking(false);
      
      // If admin but 2FA not configured, redirect to setup
      if (isAdmin && !mfaStatus.hasMFA && !mfaStatus.isEnrolled) {
        navigate('/setup-2fa');
      }
    }
  }, [isAdmin, loading, mfaStatus, navigate]);
  
  if (loading || mfaStatus.loading || checking) {
    return <LoadingSpinner message="Vérification des permissions..." />;
  }
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-destructive/10 rounded-full">
                <ShieldAlert className="w-8 h-8 text-destructive" />
              </div>
            </div>
            <CardTitle className="text-2xl">Accès Administrateur Requis</CardTitle>
            <CardDescription>
              Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Cette section est réservée aux administrateurs de la plateforme.
              </p>
            </div>
            <Button 
              onClick={() => window.location.href = '/dashboard'} 
              className="w-full"
              variant="outline"
            >
              Retour au tableau de bord
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin but 2FA not configured - will be redirected by useEffect
  if (isAdmin && !mfaStatus.hasMFA && !mfaStatus.isEnrolled) {
    return <LoadingSpinner message="Redirection vers la configuration 2FA..." />;
  }
  
  return <>{children}</>;
};
